<template>

  <popup
    v-if="addSite || siteData"
    :buttonStyle='this.addSite ? "success" : "info"'
    :heading='this.addSite ? "New Site" : "Update Site"'
    buttonSize="default"
    size="medium"
    :disable-button="!isManager('project|maintenance|property|finance|admin|management')"
    :button-text='addSite ? "Add" : "Edit"'
    :button-icon='addSite ? "fa fa-tower-broadcast"
    : "fa fa-pencil-alt"'
    :before-save="checkHandover"
    @save="onSaveDialog"
    @close="onCloseDialog">

  <!-- XOL-67 (B6): the form is SiteForm (src/components/sites/SiteForm.vue), shared with Step 5 of an RTA. This component keeps
       the Popup, the save and the reset. `formKey` remounts the form after a reset: its selects and the tenancy form read their
       value once, when they are created. -->
  <site-form
    ref="siteFormView"
    :key="formKey"
    v-model="form"
    :adding="addSite"
    :originals="originals"
    :override.sync="override"
    :handover-transition="handoverTransition"
    :errors="errors" />

</popup>

</template>

<script>



import SiteMixins from '@/mixins/SiteMixins'

import SiteForm from '@/components/sites/SiteForm'

import {ENABLE_SAVE_EVENT} from '@/components/Data'  // , DISABLE_SAVE_EVENT

export default {
  mixins: [SiteMixins],

  components: {
    SiteForm
  },


  props: {

    addSite: {
      type: Boolean,
      default: false
    },

    siteData: {
      type: Object
    },

    tableRef: {
      type: String,
      default: 'sitetable'
    },

    status: {
      type: String,
      default: null
    }

  },



  methods: {

    /**
     * XOL-67 (B6, decision 67): the state the form starts from, and goes back to. Add: a fresh `sites` struct (with the
     * status the list is filtered to), nothing in the Structure, Tenancy and Finance tabs. Edit: a copy of the site.
     *
     * resetData() used to be `this.site = this.clone(this.siteData)`, and an add has no siteData: `clone(undefined)` is
     * `JSON.parse(undefined)`, which threw, so the Sites -> Add form never reset (it kept what was typed after a save and
     * after Cancel). With decision 48 that matters: a second add would send the leftover Structure and Finance values.
     */
    initialState () {
      var site = this.addSite ? this.getTableStruct('sites') : (this.siteData ? this.clone(this.siteData) : null)
      var candidate = {}
      var loc = []

      if (!site) {
        site = this.getTableStruct('sites') || {}
      }

      if (this.addSite) {
        // XOL-67 (decision 99): the list's Candidate tab must not preselect Candidate on a new site (the API refuses it; the select no
        // longer offers it).
        if (this.status && this.status !== 'Candidate') {
          site.status = this.status
        }

        candidate.actualLat = site.latitude
        candidate.actualLng = site.longitude
      } else if (this.siteData) {
        if (this.siteData.struct_id) {
          site.structureId = this.siteData.struct_id
        }

        // XOL-67 (B8): the key must exist for Vue to see the handover status change (a site from before the API column has none).
        if (this.isCandidate(this.siteData.status) && !('handoverStatus' in site)) {
          site.handoverStatus = null
        }

        loc = [this.siteData.region, this.siteData.state, this.siteData.district, this.siteData.subdistrict]
      }

      return { site: site, struct: {}, finance: {}, tenancy: {}, candidate: candidate, loc: loc }
    },

    clearHandoverError () {
      if (Object.keys(this.errors).length > 0) {
        this.errors = {}
      }
    },

    resetData () {
      var fresh = this.initialState()

      if (this.addSite) {
        // The whole form, and a new instance of it: the selects and the tenancy form read their value once.
        this.form = fresh
        this.formKey++
      } else {
        this.form.site = fresh.site
      }
    },

    // XOL-67 (B8): the Popup's :before-save gate. It used to refuse a handover status without a date (decision 84). Decision 194 drops that
    // rule: a Candidate site may hold a status with no date (Step 5 can now store one, and the Create Sharer form shows it), and no reverse
    // rule is added. The gate stays so the errors of a previous try are cleared, and it never blocks. A date of today or earlier never
    // blocks either (decision 86: the form warns).
    checkHandover () {
      this.errors = {}

      return true
    },

    onSaveDialog () {

      var f = this.form
      var site = f.site

      site.region = this.setProp(site.region, f.loc[0])
      site.state = this.setProp(site.state, f.loc[1])
      site.district = this.setProp(site.district, f.loc[2])
      site.subdistrict = this.setProp(site.subdistrict, f.loc[3])

    /*  site.zone = site.zone.name ? site.zone.name : null */

      site.handover = this.formatDateSql(site.handover)
      site.active = this.formatDateSql(site.active)
      site.dismantled = this.formatDateSql(site.dismantled)

      site.latitude = Number(site.latitude)
      site.longitude = Number(site.longitude)

      // XOL-67 (decisions 84, 104): `handoverStatus` rides in the whole-site PUT. A cleared select cancels the scheduled change (null), and
      // so does a manual status change away from Candidate (the API enforces the same).
      if (this.handoverTransition) {
        site.handoverStatus = site.handoverStatus || null

        if (!this.isCandidate(site.status)) {
          site.handoverStatus = null
        }
      }


      if (this.addSite) {

        // XOL-67 decision 48: `struct` and `finance` are objects, so `.length >= 0` was never true and
        // the Structure and Finance tabs were never sent (the API then built both rows from the site
        // body, and what was typed was lost). Send each one when the tab holds something.
        if (Object.keys(f.struct).length > 0) {
          site.structure = f.struct
        }


        if (f.tenancy.length >= 0) {
          site.tenancy = f.tenancy
        }

        if (Object.keys(f.finance).length > 0) {
          site.finance = f.finance
        }

        if (this.candidateSite()) {
          f.candidate.markers = [{infoText: 'Option 1', icon: 'static/map/green-map.png', type: 'option', position: {lat: site.latitude, lng: site.longitude}}]
        }

      }

//      site.candidate = f.candidate
//      console.log('About to save: ', site)

      this.saveForm(this, '/sites', (this.addSite ? null : site.id), site, site.siteName, this.success)
      this.$emit('update-stats')

      // An edit goes back to the saved site at once, as it always did. An add starts over when the server has taken it
      // (success), and on Cancel: a refused add keeps what was typed, so it can be fixed and sent again.
      if (!this.addSite) {
        this.resetData()
      }
    },



    success (data) {
      this.originalSite = data
      this.$emit('change', data)
      this.$events.emit(this.tableRef + '-refresh-table')

      if (this.addSite) {
        this.resetData()
      }
    },


    onCloseDialog () {
      this.resetData()
    }

  },


  computed: {

    // XOL-67 (B8, decisions 84, 104): an edit of ANY Candidate site (the legacy site with an open RTA included) has the handover date and
    // the handover status. Judged on the saved site, so the fields do not vanish while the user moves the status in the form.
    handoverTransition () {
      return !this.addSite && !!this.siteData && this.isCandidate(this.siteData.status)
    },

    // What an Edit compares against to lock the fields that already hold a value. The Structure, Finance and Tenancy tabs
    // are the add form's: there is nothing saved to lock.
    originals () {
      return { site: this.originalSite, struct: {}, finance: {} }
    }

  },


  watch: {
    status: function (value) {
      // XOL-67 (decision 99): never Candidate on an add (see initialState).
      if (value !== 'All' && value !== 'Candidate' && this.addSite) {
        this.form.site.status = value
      }
//      console.log('status changed', this.form.site.status, value)
    },

    // An error clears when its field is edited (UX: the handover date's, or the status that asked for it).
    'form.site.handover': function () {
      this.clearHandoverError()
    },

    'form.site.handoverStatus': function () {
      this.clearHandoverError()
    }

  },



  data () {

    return {
      utilLabel: this.getUtilLabel(this.siteData),
      override: false,
      errors: {},
      form: this.initialState(),
      formKey: 0,
      originalSite: this.siteData
    }

  },  // data



  created () {

    this.$events.emit(ENABLE_SAVE_EVENT)

  }
}

</script>
